import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import styled from 'styled-components';
import { ZoomButtonWrapper } from '../../productConfig/productConfigStyles';
import useImageIntentFriction from '../protectedImage/useImageIntentFriction';
import useImageIntentZoomContent from '../protectedImage/imageIntentZoomContent';

const ImgContainer = styled.div`
  flex: 1 0 calc(25% - 24px); // 25% for 4-across grid minus gutter
  max-width: calc(25% - 24px); // Ensure 4-across grid always
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    flex: 0 1 calc(50% - 14px);
    max-width: calc(50% - 14px);
  }

  img {
    max-width: 100%;
    height: auto;
    background-color: white;
    box-shadow: 3px 3px 5px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    -webkit-user-drag: none;
    user-select: none;
    object-fit: cover;
  }
`;

const Name = styled.div`
  text-align: center;
  margin-top: 10px;
`;

const PoseItem = ({ onClick, name, intentGallery, ...rest }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'pose_picker',
  });
  // intentGallery: a pooled sibling pose's own gallery; otherwise the hook's
  // Redux defaults (the order's gallery) apply.
  const frictionContext = {
    surface: 'pose_picker',
    imageKey: rest.src,
    ...intentGallery,
  };
  // The zoomed pose renders in a portal outside this tree; it gets its own
  // friction props (surface `pose_picker_zoom`).
  const ZoomContent = useImageIntentZoomContent(frictionContext);
  const handleZoomChange = useCallback((shouldZoom) => {
    setIsZoomed(shouldZoom);
  }, []);

  const handleZoomClick = (e) => {
    e.stopPropagation();
    setIsZoomed((zoomed) => !zoomed);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      onClick();
    }
  };

  return (
    <ImgContainer onContextMenu={(event) => event.preventDefault()}>
      <ControlledZoom
        isZoomed={isZoomed}
        onZoomChange={handleZoomChange}
        ZoomContent={ZoomContent}
      >
        {/* The friction handlers sit on this wrapper, not the <img>: ImgContainer
            gives the img `pointer-events: none`, so a real right-click or
            long-press targets this div. */}
        <div
          role="button"
          onClick={onClick}
          tabIndex="0"
          onKeyDown={handleKeyDown}
          {...getFrictionProps(frictionContext)}
        >
          <img {...rest} alt={rest.alt || ''} />
        </div>
        <ZoomButtonWrapper onClick={handleZoomClick}>
          <MagnifyingGlassPlusIcon />
        </ZoomButtonWrapper>
        {name && <Name>{name}</Name>}
      </ControlledZoom>
    </ImgContainer>
  );
};

PoseItem.propTypes = {
  onClick: PropTypes.func,
  name: PropTypes.string,
  intentGallery: PropTypes.shape({
    galleryKey: PropTypes.string,
    picsGalleryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dp2GalleryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

PoseItem.defaultProps = {
  onClick: null,
  name: '',
  intentGallery: {},
};

export default PoseItem;
